import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { LoginRequest, User, LoginResponse } from '@/api/auth';
import { useLoginMutation } from '@/hooks/mutations/useLoginMutation';
import { useLogoutMutation } from '@/hooks/mutations/useLogoutMutation';
import {
  AUTH_LOCAL_STORAGE_KEYS,
  PROJECT_LOCAL_STORAGE_KEYS,
} from '@/constants/localStorage';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  getBrandId: () => string | null;
  getTokenExpiresAt: () => string | null;
  isTokenExpired: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const clearStoredAuth = () => {
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.LANDING_SESSION_TOKEN);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.USER);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.BRAND_ID);
  localStorage.removeItem(AUTH_LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_AT);
  localStorage.removeItem(PROJECT_LOCAL_STORAGE_KEYS.PROJECT_ID);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem(AUTH_LOCAL_STORAGE_KEYS.USER);
    const upstreamToken = localStorage.getItem(
      AUTH_LOCAL_STORAGE_KEYS.AUTH_TOKEN
    );
    const landingSessionToken = localStorage.getItem(
      AUTH_LOCAL_STORAGE_KEYS.LANDING_SESSION_TOKEN
    );
    const expiresAt = localStorage.getItem(
      AUTH_LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_AT
    );

    // Check if token exists and is not expired
    if (storedUser && upstreamToken && landingSessionToken && expiresAt) {
      const expiryDate = new Date(expiresAt);
      const isExpired = expiryDate < new Date();

      if (isExpired) {
        // Token expired, clear all auth data
        clearStoredAuth();
        setUser(null);
      } else {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (error) {
          console.error('Failed to parse stored user:', error);
          clearStoredAuth();
        }
      }
    } else if (storedUser || upstreamToken || landingSessionToken || expiresAt) {
      // Partial data exists, clean up
      clearStoredAuth();
    }
    setIsLoading(false);
  }, []);

  // Global handler for API 401 (token expired/invalid)
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
    };

    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', onUnauthorized);
    };
  }, []);

  // Use the custom mutation hooks
  // Set skipStorage: true to let AuthContext handle all storage logic centrally
  const loginMutation = useLoginMutation({
    skipStorage: true, // Let AuthContext handle storage
    onSuccess: (data) => {
      if (
        data.success &&
        data.data?.access_token &&
        data.data?.session_token &&
        data.data?.user
      ) {
        // Store token, user data, and token expiration
        localStorage.setItem(
          AUTH_LOCAL_STORAGE_KEYS.AUTH_TOKEN,
          data.data.access_token
        );
        localStorage.setItem(
          AUTH_LOCAL_STORAGE_KEYS.LANDING_SESSION_TOKEN,
          data.data.session_token
        );
        localStorage.setItem(
          AUTH_LOCAL_STORAGE_KEYS.USER,
          JSON.stringify(data.data.user)
        );
        if (data.data.token_expires_at) {
          localStorage.setItem(
            AUTH_LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_AT,
            data.data.token_expires_at
          );
        }
        setUser(data.data.user);
        window.dispatchEvent(new Event('auth:login-success'));
        console.log('✅ Authentication successful, token saved');
      } else {
        console.warn('⚠️ Login response missing required data:', data);
      }
    },
    onError: (error) => {
      console.error('❌ Login failed:', error);
    },
  });

  const logoutMutation = useLogoutMutation({
    onSuccess: () => {
      // Clear all auth-related data
      clearStoredAuth();
      setUser(null);
    },
  });

  const handleLogin = async (
    credentials: LoginRequest
  ): Promise<LoginResponse> => {
    return await loginMutation.mutateAsync(credentials);
  };

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
  };

  // Helper functions to get stored data
  const getBrandId = () => {
    return localStorage.getItem(AUTH_LOCAL_STORAGE_KEYS.BRAND_ID);
  };

  const getTokenExpiresAt = () => {
    return localStorage.getItem(AUTH_LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_AT);
  };

  const isTokenExpired = () => {
    const expiresAt = localStorage.getItem(
      AUTH_LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_AT
    );
    if (!expiresAt) return true;

    const expiryDate = new Date(expiresAt);
    return expiryDate < new Date();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading: isLoading || loginMutation.isPending || logoutMutation.isPending,
    login: handleLogin,
    logout: handleLogout,
    setUser,
    getBrandId,
    getTokenExpiresAt,
    isTokenExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
